'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { stripHtml } from '@/lib/homepage-mappers';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import type { ProcessColoredHeading, ProcessNavGroup } from '@/types/process';

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BRAND_RED = '#EC1C24';

function heroAnim(name: string, duration: string, delay: string, easing = EASE): string {
  return `${name} ${duration} ${easing} ${delay} both`;
}

interface ProcessHeroProps {
  eyebrowTitle?: string;
  descriptionHtml?: string;
  bannerHeading?: ProcessColoredHeading[];
  processNav?: ProcessNavGroup | null;
}

function isAccentColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const normalized = color.trim().toUpperCase();
  return normalized === '#EC1C24' || normalized === '#ED1C22' || normalized === '#E31E24';
}

type StageNavItem = { stepNumber: string; title: string; anchorId: string };

/** The numbered process-stage navigation grid (shared by mobile + desktop layouts). */
function StagesNav({
  variant,
  navHeading,
  navItems,
  mounted,
  onSelect,
}: {
  variant: 'desktop' | 'mobile';
  navHeading: string;
  navItems: StageNavItem[];
  mounted: boolean;
  onSelect: (anchorId: string) => void;
}) {
  return (
    <div className={variant === 'desktop' ? 'w-[min(100%,340px)]' : 'w-full'}>
      {navHeading ? (
        <p className="mb-7 text-[9px] font-semibold uppercase tracking-[0.38em] text-[#111111] sm:mb-8">
          {navHeading}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-x-8 gap-y-8 sm:gap-x-10 sm:gap-y-9 lg:gap-x-12 lg:gap-y-10">
        {navItems.map((item, index) => (
          <button
            key={`${variant}-${item.anchorId}`}
            type="button"
            onClick={() => onSelect(item.anchorId)}
            className="group cursor-pointer border-none bg-transparent p-0 text-left transition-transform duration-300 hover:translate-x-0.5"
            style={
              variant === 'desktop' && mounted
                ? {
                    opacity: 1,
                    animation: heroAnim('process-pop-in', '0.5s', `${0.52 + index * 0.07}s`),
                  }
                : undefined
            }
          >
            <div className="text-[clamp(2.35rem,4.5vw,3.25rem)] font-bold leading-none tracking-tight text-[#EC1C24]/[0.2] transition-colors duration-300 group-hover:text-[#EC1C24]/35">
              {item.stepNumber}
            </div>
            <div className="mt-1.5 text-[13px] font-bold leading-tight text-[#111111] transition-colors duration-300 group-hover:text-[#EC1C24] sm:text-[14px]">
              {item.title}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** The small uppercase eyebrow label above the hero story copy. */
function HeroEyebrow({
  eyebrowTitle,
  hasApiStory,
  fallbackLabel,
}: {
  eyebrowTitle?: string;
  hasApiStory: boolean;
  fallbackLabel: string;
}) {
  let text: string | null;
  if (eyebrowTitle?.trim()) {
    text = eyebrowTitle;
  } else if (!hasApiStory) {
    text = fallbackLabel;
  } else {
    text = null;
  }
  if (text === null) return null;
  return (
    <div className="mb-3 flex items-center gap-2.5 text-[8px] font-semibold uppercase tracking-[0.35em] text-black/28">
      <div className="h-px w-[18px] bg-black/20" />
      {text}
    </div>
  );
}

export default function ProcessHero({
  eyebrowTitle,
  descriptionHtml,
  bannerHeading,
  processNav,
}: ProcessHeroProps) {
  const t = useTranslations('ourProces.hero');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setMounted(true), 80);
    return () => window.clearTimeout(timer);
  }, []);

  const hasApiStory = Boolean(descriptionHtml?.trim());
  const storyText = hasApiStory ? stripHtml(descriptionHtml) : t('story');

  const navItems = useMemo(
    () =>
      (processNav?.navItems ?? []).map((item) => ({
        stepNumber: item.stepNumber,
        title: item.title,
        anchorId: item.anchorId,
      })),
    [processNav],
  );

  const navHeading = processNav?.heading?.trim() ?? '';
  const hasApiNav = navItems.length > 0;

  const headingParts = useMemo(() => bannerHeading ?? [], [bannerHeading]);
  const hasApiHeading = headingParts.length > 0;

  const scrollToStage = (anchorId: string) => {
    const target = document.getElementById(anchorId);
    if (!target) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const scrollDown = () => scrollToStage(navItems[0]?.anchorId ?? 'section1');

  const renderHeadingPart = (part: ProcessColoredHeading, index: number) => {
    const accent = isAccentColor(part.textColor);
    return (
      <span
        key={part.id ?? index}
        className="block"
        style={{
          color: accent ? BRAND_RED : part.textColor || '#111111',
          animation:
            mounted && index > 0
              ? heroAnim('process-slide-in-left', '0.75s', `${0.34 + index * 0.08}s`)
              : undefined,
        }}
      >
        {part.text}
      </span>
    );
  };

  const renderStagesNav = (variant: 'desktop' | 'mobile') => {
    if (!hasApiNav) return null;
    return (
      <StagesNav
        variant={variant}
        navHeading={navHeading}
        navItems={navItems}
        mounted={mounted}
        onSelect={scrollToStage}
      />
    );
  };

  return (
    <section
      data-section-blur="false"
      className="relative flex min-h-svh flex-col overflow-hidden bg-[#F0EDE8] pt-[var(--site-header-height)] text-[#111111]"
      aria-labelledby="our-process-hero-heading"
    >
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(135deg,transparent,transparent 48px,rgba(0,0,0,0.006) 48px,rgba(0,0,0,0.006) 49px)',
        }}
      />

      <div
        className="pointer-events-none absolute -left-[10%] top-[-15%] z-0 h-[55vh] w-[55vh] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(236,28,36,0.09) 0%, transparent 68%)',
          animation: mounted ? 'process-hero-glow-pulse 6s ease-in-out infinite' : 'none',
        }}
      />

      {[68, 44].map((size, index) => {
        const ringDirection = index === 1 ? 'reverse' : 'normal';
        const ringAnimation = `process-hero-ring-spin ${28 + index * 8}s linear infinite ${ringDirection}`;
        return (
          <div
            key={size}
            className="pointer-events-none absolute right-[-2vw] top-1/2 z-0 -translate-y-1/2 rounded-full border border-black/[0.06]"
            style={{
              width: `${size}vh`,
              height: `${size}vh`,
              opacity: mounted ? 1 : 0,
              animation: mounted ? ringAnimation : 'none',
              transition: `opacity 1.4s ease ${0.5 + index * 0.15}s`,
            }}
          />
        );
      })}

      <div
        className="pointer-events-none absolute bottom-0 right-0 z-0 h-[clamp(180px,28vw,280px)] w-[clamp(300px,48vw,480px)] bg-contain bg-right-bottom bg-no-repeat transition-opacity duration-[1400ms] ease-out delay-500"
        style={{
          backgroundImage: "url('/images/wonder_lg.svg')",
          opacity: mounted ? 0.08 : 0,
        }}
        aria-hidden
      />

      <div className="relative z-[1] grid min-h-[calc(100svh-var(--site-header-height))] grid-cols-1 items-center lg:grid-cols-[minmax(0,1.05fr)_auto_minmax(0,0.95fr)]">
        <div
          className="absolute bottom-[10%] left-0 top-[10%] hidden w-[3px] bg-gradient-to-b from-transparent via-[#EC1C24] to-transparent transition-opacity duration-700 delay-300 lg:block"
          style={{ opacity: mounted ? 0.85 : 0 }}
        />

        {/* Left — hero copy from API */}
        <div className="flex flex-col justify-center px-6 py-8 sm:px-8 sm:py-10 lg:px-[4.5rem] lg:py-12">
          <div
            className="mb-6 sm:mb-8 lg:mb-10"
            style={{
              opacity: mounted ? undefined : 0,
              animation: mounted ? heroAnim('process-slide-in-up', '0.7s', '0.05s') : 'none',
            }}
          >
            <HeroEyebrow
              eyebrowTitle={eyebrowTitle}
              hasApiStory={hasApiStory}
              fallbackLabel={t('estLabel')}
            />
            <p className="max-w-[460px] text-[13px] font-medium leading-[1.8] text-black sm:text-[14.5px] sm:leading-[1.85]">
              {storyText}
            </p>
          </div>

          <div
            className="mb-8 h-px w-10 bg-black/12"
            style={{
              opacity: mounted ? undefined : 0,
              animation: mounted ? heroAnim('process-expand-line', '0.55s', '0.2s') : 'none',
            }}
          />

          <h1
            id="our-process-hero-heading"
            className="hero-title mb-6 text-[#111111] sm:mb-8"
            style={{
              opacity: mounted ? undefined : 0,
              animation: mounted ? heroAnim('process-slide-in-left', '0.75s', '0.34s') : 'none',
            }}
          >
            {hasApiHeading ? (
              headingParts.map(renderHeadingPart)
            ) : (
              <>
                <span className="block text-[#111111]">{t('headlinePrimary')}</span>
                <span
                  className="block text-[#EC1C24]"
                  style={{
                    animation: mounted
                      ? heroAnim('process-slide-in-left', '0.75s', '0.42s')
                      : 'none',
                  }}
                >
                  {t('headlineAccent')}
                </span>
              </>
            )}
          </h1>

          <div
            style={{
              opacity: mounted ? undefined : 0,
              animation: mounted ? heroAnim('process-cta-bounce-in', '0.7s', '0.58s') : 'none',
            }}
          >
            <SwapTextButton
              text={t('cta')}
              hoverText={t('ctaHover')}
              arrowDirection="down"
              onClick={scrollDown}
              className="shadow-[0_6px_28px_rgba(236,28,36,0.26)]"
            />
          </div>

          <div className="mt-10 lg:hidden">{renderStagesNav('mobile')}</div>
        </div>

        {/* Center — processNav from API */}
        <div
          className="hidden justify-center px-6 lg:flex lg:px-4 xl:px-8"
          style={{
            opacity: mounted ? undefined : 0,
            animation: mounted ? heroAnim('process-fade-in', '0.85s', '0.45s', 'ease') : 'none',
          }}
        >
          {renderStagesNav('desktop')}
        </div>

        {/* Right — decorative space for rings */}
        <div className="hidden lg:block" aria-hidden />
      </div>
    </section>
  );
}
